import { Divider, Flex, Text } from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import React from 'react';

import { lienholderDisplayName } from '../constants/lienholders';
import { validationSchema } from '../pages/PayoffInstructions/utils';
import { ONE_DAY_MS, checkLastDayOfMonth } from '../utils/dates';
import { isPayoffBelowFloor } from '../utils/validation/payoffAmount';
import LienholderPayoffInstructions from './LienholderPayoffInstructions';
import SelfServePayoffLowAmountWarning from './SelfServePayoffLowAmountWarning';
import DateInput from './formComponents/DateInput';
import NumberInput from './formComponents/NumberInput';

type FormValues = {
  totalPayoff?: number;
  goodThroughDate?: Date;
};

export type SelfServePayoffSubmitValues = {
  vehiclePayoff: number;
  goodThroughDate: Date;
};

export type SelfServePayoffSubmitState = {
  isSubmitting: boolean;
  isValid: boolean;
};

type SelfServePayoffFormBodyProps = {
  onSubmit: (values: SelfServePayoffSubmitValues) => Promise<void>;
  lienholderName?: string | null;
  lienholderSlug?: string | null;
  retailBookValue?: number | null;
  submitContent: (state: SelfServePayoffSubmitState) => React.ReactNode;
  belowFields?: React.ReactNode;
};

const INITIAL_VALUES: FormValues = {
  totalPayoff: undefined,
  goodThroughDate: undefined,
};

/**
 * Shared interior of the self-serve payoff form: amount + good-through-date
 * inputs and lienholder-specific instructions. Consumed by
 * SelfServePayoffModal (modal chrome) and by the marketplace pre-list
 * payoff accordion item (inline). Caller supplies submit-area content.
 */
const SelfServePayoffFormBody = ({
  onSubmit,
  lienholderName,
  lienholderSlug,
  retailBookValue,
  submitContent,
  belowFields,
}: SelfServePayoffFormBodyProps) => {
  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    if (
      values.totalPayoff == null ||
      !values.goodThroughDate ||
      isPayoffBelowFloor(values.totalPayoff, retailBookValue)
    ) {
      setSubmitting(false);
      return;
    }
    try {
      await onSubmit({
        vehiclePayoff: values.totalPayoff,
        goodThroughDate: values.goodThroughDate,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const lienholderLabel = lienholderDisplayName(lienholderName);

  return (
    <Formik<FormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={validationSchema}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, submitCount, values }) => {
        const showLowAmountWarning =
          submitCount > 0 && isPayoffBelowFloor(values.totalPayoff, retailBookValue);
        return (
          <Form>
            <Flex gap={2} alignItems="flex-start">
              <NumberInput
                type="tel"
                _input={{ inputMode: 'decimal' }}
                _container={{ textAlign: 'left', w: { base: '100%', md: '50%' } }}
                label="PAYOFF AMOUNT"
                name="totalPayoff"
                placeholder="Payoff Amount"
                isMoney
                labelProps={{ fontSize: { base: '12px', md: '14px' } }}
              />
              <DateInput
                hideCalendar
                label="GOOD-THROUGH DATE"
                name="goodThroughDate"
                _container={{ w: { base: '100%', md: '50%' } }}
                labelProps={{ fontSize: { base: '12px', md: '14px' } }}
                openToDate={checkLastDayOfMonth() ? new Date(Date.now() + ONE_DAY_MS) : undefined}
              />
            </Flex>
            {belowFields || <Divider color="grayDarkBackground" my={4} />}
            <Text
              fontSize="16px"
              fontWeight="semibold"
              textAlign="left"
              textTransform="uppercase"
              mb={2}
            >
              How to find your payoff from {lienholderLabel}:
            </Text>
            <LienholderPayoffInstructions lienholderSlug={lienholderSlug} />
            <Flex flexDir="column" alignItems="center" gap={4} mt={2}>
              {showLowAmountWarning && <SelfServePayoffLowAmountWarning />}
              {submitContent({ isSubmitting, isValid })}
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SelfServePayoffFormBody;
